// Schema.org JSON-LD injection. Kept separate from the head tag mutation in
// src/lib/seo.ts so JSON-LD and runtime OG/Twitter handling stay decoupled.

import type { Article } from '@/mocks/articles';
import type { FaqItem } from '@/mocks/faqs';
import { SITE_URL, SITE_NAME, FAVICON_URL } from './seo';

export const SCHEMA_IDS = [
  'ld-json-organization',
  'ld-json-website',
  'ld-json-article',
  'ld-json-breadcrumb',
  'ld-json-faq',
];

function injectJsonLd(id: string, json: Record<string, unknown>): void {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(json);
  document.head.appendChild(script);
}

export function clearSchemas(): void {
  SCHEMA_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });
}

const publisher = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: FAVICON_URL },
};

export function injectOrganizationSchema(): void {
  injectJsonLd('ld-json-organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: FAVICON_URL,
  });
}

export function injectWebSiteSchema(): void {
  injectJsonLd('ld-json-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  });
}

export function injectArticleSchema(
  article: Article,
  lang: string,
  title: string,
  description: string
): void {
  const inLanguage = lang === 'pt' ? 'pt-BR' : lang === 'de' ? 'de' : 'en';
  injectJsonLd('ld-json-article', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: [article.image],
    datePublished: article.updatedDate,
    dateModified: article.updatedDate,
    inLanguage,
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher,
    mainEntityOfPage: `${SITE_URL}/article/${article.slug}`,
  });
}

export function injectBreadcrumbSchema(items: { name: string; path: string }[]): void {
  injectJsonLd('ld-json-breadcrumb', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.path === '/' ? SITE_URL : `${SITE_URL}${item.path}`,
    })),
  });
}

export function injectFaqSchema(qa: FaqItem[]): void {
  injectJsonLd('ld-json-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  });
}